import { RefObject, useEffect, useRef } from 'react';
import { ScrollAnchor } from './types';

export const captureScrollPosition = (container: HTMLDivElement): ScrollAnchor | null => {
  const containerRect = container.getBoundingClientRect();
  const cards = container.querySelectorAll('[data-comment-id]');

  // Iterate from bottom to top to find last fully visible card
  for (let i = cards.length - 1; i >= 0; i--) {
    const card = cards[i] as HTMLElement;
    const cardRect = card.getBoundingClientRect();

    // Check if card is fully visible within container
    if (cardRect.top >= containerRect.top && cardRect.bottom <= containerRect.bottom) {
      const itemId = card.getAttribute('data-comment-id');
      if (itemId) {
        const offset = containerRect.bottom - cardRect.bottom;
        return { itemId: parseInt(itemId, 10), offset };
      }
    }
  }
  return null;
};

export const restoreScrollPosition = (container: HTMLDivElement, anchor: ScrollAnchor) => {
  const targetCard = container.querySelector(`[data-comment-id="${anchor.itemId}"]`) as HTMLElement;
  if (targetCard) {
    const containerRect = container.getBoundingClientRect();
    const cardRect = targetCard.getBoundingClientRect();

    // Calculate desired position: card bottom should be offsetFromBottom from container bottom
    const targetScrollTop = container.scrollTop + (cardRect.bottom - containerRect.bottom) + anchor.offset;
    container.scrollTop = targetScrollTop;
  }
};

export function useScrollAnchor(containerRef: RefObject<HTMLDivElement>): ScrollAnchor | null {
  const scrollStateRef = useRef<ScrollAnchor | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refs are fine
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollStateRef.current = captureScrollPosition(container);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  return scrollStateRef.current;
}
